import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Loader2, Search, Radio, ExternalLink, Trash2, RefreshCw,
  Mail, Phone, Download, FileSpreadsheet, CheckSquare, Square,
  X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ChatterRow {
  id: string;
  source_url: string;
  source_domain: string | null;
  title: string | null;
  snippet: string | null;
  search_query: string;
  ai_score: number | null;
  ai_reasoning: string | null;
  ai_seller_signals: string[] | null;
  extracted_email: string | null;
  extracted_phone: string | null;
  status: string;
  notes: string | null;
  posted_date: string | null;
  created_at: string;
}


const PRESET_QUERIES = [
  '"sell my land contract"',
  '"sell my mortgage note"',
  '"cash out my note" owner financing',
  '"selling my promissory note"',
  '"need to sell" "land contract"',
  'site:reddit.com "sell" "land contract"',
  'site:biggerpockets.com selling note for cash',
  'site:craigslist.org "land contract" "for sale by owner"',
  '"holding a note" want to sell',
  '"balloon payment" "selling" note',
];

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-500",
  reviewed: "bg-amber-500",
  contacted: "bg-purple-500",
  converted: "bg-green-600",
  dismissed: "bg-gray-400",
};

// Domains that are almost never an actual seller posting their own note
const BAD_DOMAINS = new Set([
  "biggerpockets.com",
  "notebuyer.com",
  "mortgagebuyer.com",
  "crexi.com",
  "loopnet.com",
  "realtor.com",
  "zillow.com",
  "redfin.com",
  "trulia.com",
  "apartments.com",
  "craigslist.org", // keep some listings but ads often dominate
  "ebay.com",
  "etsy.com",
  "amazon.com",
  "youtube.com",
  "linkedin.com",
  "facebook.com",
  "wikipedia.org",
  "investopedia.com",
]);

const OUTREACH_SUBJECT = "Cash offer for your note / land contract";
function outreachBody(url: string) {
  return `Hi,

I came across your post about selling a note/land contract. I'm a direct note buyer and I pay cash for owner-financed notes and land contracts. I can usually give you a written offer within 24 hours.

If you'd like a no-obligation quote, just reply with a few details (balance, monthly payment, interest rate) or give me a call.

Thanks,
Mike
CFG Investor
Phone: 517-908-0433
${url}`;
}

export default function NoteSellerChatterPage() {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(15);
  const [tbs, setTbs] = useState<string>("");
  const [searching, setSearching] = useState(false);
  const [rows, setRows] = useState<ChatterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [minScore, setMinScore] = useState<number>(0);
  const [requireContactInfo, setRequireContactInfo] = useState(false);
  const [excludeBadDomains, setExcludeBadDomains] = useState(false);
  const [ageDays, setAgeDays] = useState<number>(0);
  const [sortBy, setSortBy] = useState<"score" | "date">("score");
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  const changeSearchTime = (value: string) => {
    setTbs(value === "any" ? "" : value);
    setAgeDays({ any: 0, "qdr:d": 1, "qdr:w": 7, "qdr:m": 30, "qdr:y": 365 }[value] ?? 0);
  };

  const fetchRows = async () => {
    setLoading(true);
    let q = supabase
      .from("note_seller_chatter")
      .select("*")
      .limit(500);

    if (sortBy === "date") {
      q = q.order("created_at", { ascending: false });
    } else {
      q = q
        .order("ai_score", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
    }

    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    if (minScore > 0) q = q.gte("ai_score", minScore);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
    } else {
      setRows((data || []) as ChatterRow[]);
      setSelectedRowIds(new Set());
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, minScore, ageDays, sortBy]);

  const filteredRows = useMemo(() => {
    const cutoff = ageDays > 0 ? Date.now() - ageDays * 86400000 : null;
    return rows.filter((r) => {
      if (requireContactInfo && !r.extracted_email && !r.extracted_phone) return false;
      if (excludeBadDomains && r.source_domain && BAD_DOMAINS.has(r.source_domain.toLowerCase())) return false;
      if (cutoff) {
        // Use the published date when known, otherwise fall back to when we
        // found it so results without a detected date are still visible.
        const raw = r.posted_date || r.created_at;
        const when = new Date(raw).getTime();
        if (!Number.isNaN(when) && when < cutoff) return false;
      }
      return true;
    });
  }, [rows, requireContactInfo, excludeBadDomains, ageDays]);


  const scoreBreakdown = useMemo(() => {
    const buckets = { "10": 0, "7-9": 0, "4-6": 0, "1-3": 0, none: 0 };
    filteredRows.forEach((r) => {
      if (r.ai_score == null) buckets.none++;
      else if (r.ai_score >= 10) buckets["10"]++;
      else if (r.ai_score >= 7) buckets["7-9"]++;
      else if (r.ai_score >= 4) buckets["4-6"]++;
      else buckets["1-3"]++;
    });
    return buckets;
  }, [filteredRows]);

  const allSelected = filteredRows.length > 0 && selectedRowIds.size === filteredRows.length;
  const someSelected = selectedRowIds.size > 0 && selectedRowIds.size < filteredRows.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(filteredRows.map((r) => r.id)));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateRow = async (id: string, patch: Partial<ChatterRow>) => {
    const { error } = await supabase.from("note_seller_chatter").update(patch).eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateRows = async (ids: string[], patch: Partial<ChatterRow>) => {
    if (ids.length === 0) return;
    const { error } = await supabase.from("note_seller_chatter").update(patch).in("id", ids);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, ...patch } : r)));
  };

  const deleteRow = async (id: string) => {
    const { error } = await supabase.from("note_seller_chatter").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    toast.success("Deleted");
  };

  const bulkDismiss = async () => {
    if (selectedRowIds.size === 0) return;
    await updateRows(Array.from(selectedRowIds), { status: "dismissed" });
    toast.success(`${selectedRowIds.size} dismissed`);
    setSelectedRowIds(new Set());
  };

  const exportCSV = () => {
    const data = filteredRows;
    const headers = ["Score", "Status", "Title", "URL", "Domain", "Email", "Phone", "AI Reasoning", "Search Query", "Created At"];
    const rows = data.map((r) => [
      r.ai_score ?? "",
      r.status,
      (r.title || "").replace(/"/g, '""'),
      r.source_url,
      r.source_domain || "",
      r.extracted_email || "",
      r.extracted_phone || "",
      (r.ai_reasoning || "").replace(/"/g, '""'),
      r.search_query,
      r.created_at,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `note-seller-chatter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${data.length} rows exported`);
  };

  const runSearch = async () => {
    if (!query.trim() || query.trim().length < 3) {
      toast.error("Enter a search query (min 3 chars)");
      return;
    }
    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-note-seller-chatter", {
        body: { query: query.trim(), limit, tbs: tbs || undefined },
      });
      if (error) throw error;
      if (data?.success) {
        toast.success(data.message || "Search complete");
        await fetchRows();
      } else {
        toast.error(data?.error || "Search failed");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const contactByEmail = async (r: ChatterRow) => {
    if (!r.extracted_email) return;
    const subject = encodeURIComponent(OUTREACH_SUBJECT);
    const body = encodeURIComponent(outreachBody(r.source_url));
    window.open(`mailto:${r.extracted_email}?subject=${subject}&body=${body}`, "_blank");
    if (r.status !== "contacted" && r.status !== "converted") {
      await updateRow(r.id, { status: "contacted" });
    }
    toast.success("Email client opened; status marked contacted");
  };

  const contactByPhone = async (r: ChatterRow) => {
    if (!r.extracted_phone) return;
    window.open(`tel:${r.extracted_phone.replace(/[^0-9+]/g, "")}`);
    if (r.status !== "contacted" && r.status !== "converted") {
      await updateRow(r.id, { status: "contacted" });
    }
    toast.success("Phone dialer opened; status marked contacted");
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Radio className="h-7 w-7 text-primary" />
          Note Seller Chatter
        </h1>
        <p className="text-muted-foreground mt-1">
          Scan the web for people talking about selling their notes or land contracts. AI scores each hit 1–10 for seller intent.
        </p>
      </div>

      <Card className="p-6 space-y-4">
        <div className="space-y-2">
          <Label>Search Query</Label>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='e.g. "sell my land contract" Michigan'
              onKeyDown={(e) => {
                if (e.key === "Enter" && !searching) runSearch();
              }}
            />
            <Button onClick={runSearch} disabled={searching}>
              {searching ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Scanning...</>
              ) : (
                <><Search className="mr-2 h-4 w-4" />Scan Web</>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Results to fetch</Label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="30">30 (max)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Time filter</Label>
            <Select value={tbs || "any"} onValueChange={changeSearchTime}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any time</SelectItem>
                <SelectItem value="qdr:d">Past 24 hours</SelectItem>
                <SelectItem value="qdr:w">Past week</SelectItem>
                <SelectItem value="qdr:m">Past month</SelectItem>
                <SelectItem value="qdr:y">Past year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Quick presets (click to use)</Label>
          <div className="flex flex-wrap gap-2">
            {PRESET_QUERIES.map((p) => (
              <Button
                key={p}
                variant="outline"
                size="sm"
                onClick={() => setQuery(p)}
                className="text-xs h-7"
              >
                {p}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min score</Label>
            <Select value={String(minScore)} onValueChange={(v) => setMinScore(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Any</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="6">6+</SelectItem>
                <SelectItem value="8">8+ (best)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Age</Label>
            <Select value={String(ageDays)} onValueChange={(v) => setAgeDays(Number(v))}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Any time</SelectItem>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Sort by</Label>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as "score" | "date")}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Best score</SelectItem>
                <SelectItem value="date">Newest first</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="require-contact"
              checked={requireContactInfo}
              onCheckedChange={setRequireContactInfo}
            />
            <Label htmlFor="require-contact" className="text-xs cursor-pointer">Must have email/phone</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="exclude-bad-domains"
              checked={excludeBadDomains}
              onCheckedChange={setExcludeBadDomains}
            />
            <Label htmlFor="exclude-bad-domains" className="text-xs cursor-pointer">Hide known ad sites</Label>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <div className="ml-auto text-sm text-muted-foreground">
            {filteredRows.length} of {rows.length} result{filteredRows.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(scoreBreakdown).map(([bucket, count]) => (
            <Badge key={bucket} variant="outline" className="text-xs">
              {bucket === "none" ? "Unscored" : bucket}: {count}
            </Badge>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSelectAll}
            disabled={filteredRows.length === 0}
          >
            {allSelected ? <CheckSquare className="mr-2 h-4 w-4" /> : someSelected ? <Square className="mr-2 h-4 w-4" /> : <Square className="mr-2 h-4 w-4" />}
            {allSelected ? "Unselect all" : "Select all"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={bulkDismiss}
            disabled={selectedRowIds.size === 0}
            className="text-destructive hover:text-destructive"
          >
            <X className="mr-2 h-4 w-4" />
            Dismiss {selectedRowIds.size > 0 ? `(${selectedRowIds.size})` : ""}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            disabled={filteredRows.length === 0}
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No chatter matches your filters. Run a search or adjust filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-16">Score</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="w-40">Contact</TableHead>
                  <TableHead className="w-32">Source</TableHead>
                  <TableHead className="w-40">Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((r) => (
                  <TableRow key={r.id} className={selectedRowIds.has(r.id) ? "bg-muted/50" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRowIds.has(r.id)}
                        onCheckedChange={() => toggleSelectRow(r.id)}
                        aria-label={`Select ${r.title || r.source_url}`}
                      />
                    </TableCell>
                    <TableCell>
                      {r.ai_score != null ? (
                        <Badge
                          className={`${
                            r.ai_score >= 8 ? "bg-green-600" :
                            r.ai_score >= 6 ? "bg-amber-500" :
                            r.ai_score >= 4 ? "bg-blue-500" : "bg-gray-400"
                          } text-white`}
                        >
                          {r.ai_score}
                        </Badge>
                      ) : (
                        <Badge variant="outline">—</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 max-w-xl">
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-primary hover:underline flex items-start gap-1"
                        >
                          {r.title || r.source_url}
                          <ExternalLink className="h-3 w-3 mt-1 flex-shrink-0" />
                        </a>
                        {r.snippet && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{r.snippet}</p>
                        )}
                        {r.ai_reasoning && (
                          <p className="text-xs italic text-muted-foreground">
                            <span className="font-semibold">AI:</span> {r.ai_reasoning}
                          </p>
                        )}
                        {r.ai_seller_signals && r.ai_seller_signals.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.ai_seller_signals.map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                        <Textarea
                          placeholder="Notes..."
                          value={r.notes || ""}
                          onChange={(e) => setRows((prev) => prev.map((row) => row.id === r.id ? { ...row, notes: e.target.value } : row))}
                          onBlur={(e) => updateRow(r.id, { notes: e.target.value })}
                          className="text-xs mt-2 min-h-[40px]"
                          rows={1}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {r.extracted_email ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs w-full justify-start"
                            onClick={() => contactByEmail(r)}
                          >
                            <Mail className="h-3 w-3 mr-1 text-primary" />
                            {r.extracted_email.length > 22 ? r.extracted_email.slice(0, 22) + "…" : r.extracted_email}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">No email</span>
                        )}
                        {r.extracted_phone ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs w-full justify-start"
                            onClick={() => contactByPhone(r)}
                          >
                            <Phone className="h-3 w-3 mr-1 text-primary" />
                            {r.extracted_phone}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">No phone</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>{r.source_domain}</div>
                      <div className="mt-1">
                        {r.posted_date
                          ? `Posted ${new Date(r.posted_date).toLocaleDateString()}`
                          : `Found ${new Date(r.created_at).toLocaleDateString()}`}
                      </div>
                      <div className="mt-1 italic">"{r.search_query}"</div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={r.status}
                        onValueChange={(v) => updateRow(r.id, { status: v })}
                      >
                        <SelectTrigger className="h-8">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${STATUS_COLORS[r.status] || "bg-gray-400"}`} />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="dismissed">Dismissed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRow(r.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
